import React, { useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks';
import { LoadingSpinner } from '@/components';
import LandingPage from '@/pages/HomePage';
import { ErrorPage } from '@/pages/ErrorPage';
import { isDev } from './constants';
import { MobileRouteGuard } from '@/mobile/MobileRouteGuard';
import { isMobilePhone } from '@/mobile/detection/deviceDetection';

// Lazy load mobile components for code splitting
const MobileHome = React.lazy(() =>
  import('@/mobile/MobileHome').then((module) => ({ default: module.MobileHome })),
);
const MobileStudio = React.lazy(() =>
  import('@/mobile/MobileStudio').then((module) => ({ default: module.MobileStudio })),
);

function MainPage() {
  const { isAuthenticated, isLoading } = useUser();
  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }
  return <LandingPage />;
}

/**
 * MobilePage - Wrapper for mobile routes that handles authentication
 * This mirrors MainPage but for mobile routes, ensuring useUser() is called
 * to populate the UserContext with isSuperUser and other auth data.
 */
function MobilePage({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useUser();
  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }
  return <>{children}</>;
}

/**
 * MobileHomeRedirect - Redirects mobile phones from root to /mobile/studio
 */
function MobileHomeRedirect({ children }: { children: React.ReactNode }) {
  const history = useHistory();
  const location = useLocation();
  const isPhone = isMobilePhone();

  useEffect(() => {
    if (isPhone && location.pathname === '/') {
      // Redirect mobile phones to mobile studio
      history.replace(`/mobile/studio${location.search}${location.hash}`);
    }
  }, [isPhone, location, history]);

  if (isPhone && location.pathname === '/') {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
}

function Routing() {
  return (
    <Switch>
      {/* Mobile routes - placed first for priority */}
      <Route exact path="/mobile">
        <MobilePage>
          <React.Suspense fallback={<LoadingSpinner />}>
            <MobileHome />
          </React.Suspense>
        </MobilePage>
      </Route>
      <Route exact path="/mobile/studio">
        <MobilePage>
          <React.Suspense fallback={<LoadingSpinner />}>
            <MobileStudio />
          </React.Suspense>
        </MobilePage>
      </Route>

      {/* Desktop routes with mobile phone auto-redirect on /studio */}
      <Route exact path="/studio">
        <MobileRouteGuard>
          <MainPage />
        </MobileRouteGuard>
      </Route>

      {/* Root route with mobile phone redirect to /mobile/studio */}
      <Route exact path="/">
        <MobileHomeRedirect>
          <MainPage />
        </MobileHomeRedirect>
      </Route>

      {/* Other desktop routes without mobile redirect */}
      <Route
        exact
        path={['/devices', '/live', '/settings', '/settings/*', ...(isDev ? ['/demo'] : [])]}
      >
        <MainPage />
      </Route>

      {/* 404 handler */}
      <Route>
        <ErrorPage error={{ message: '404', name: '' }} resetErrorBoundary={() => {}} />
      </Route>
    </Switch>
  );
}

export default Routing;
